import { useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useStates, State, StateInsert } from "@/features/locations/hooks/useStates";
import { useCountries } from "@/features/locations/hooks/useCountries";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

export default function States() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const { states, isLoading, createState, updateState, deleteState, isCreating, isUpdating, isDeleting } = useStates();
  const { countries } = useCountries();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [formData, setFormData] = useState<StateInsert>({ nome: "", uf: "", paisId: null, codigoIbge: null, isAtivo: true, aliquotaIbs: 0.10 });
  const [busca, setBusca] = useState("");

  const termo = busca.trim().toLowerCase();
  const statesFiltrados = termo
    ? (states ?? []).filter(s =>
        (s.nome ?? "").toLowerCase().includes(termo) ||
        (s.uf ?? "").toLowerCase().includes(termo) ||
        (s.pais?.nome ?? "").toLowerCase().includes(termo))
    : (states ?? []);

  const paginacao = usePaginacaoLocal(statesFiltrados);

  const openNew = () => {
    setEditingState(null);
    setFormData({ nome: "", uf: "", paisId: null, codigoIbge: null, isAtivo: true, aliquotaIbs: 0.10 });
    setDialogOpen(true);
  };

  const openEdit = (state: State) => {
    setEditingState(state);
    setFormData({ nome: state.nome, uf: state.uf, paisId: state.paisId, codigoIbge: state.codigoIbge, isAtivo: state.isAtivo, aliquotaIbs: state.aliquotaIbs });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (editingState) {
      updateState({ id: editingState.id, data: formData });
    } else {
      createState(formData);
    }
    setDialogOpen(false);
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteState(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Estados</h1>
          <p className="text-muted-foreground">Gerencie os estados cadastrados</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo Estado</Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por nome, UF ou país..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>Cód. IBGE</TableHead>
              <TableHead>País</TableHead>
              <TableHead className="text-right">IBS (%)</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center">Carregando...</TableCell></TableRow>
            ) : paginacao.itensPaginados.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">{busca ? "Nenhum estado encontrado para esta busca." : "Nenhum estado cadastrado"}</TableCell></TableRow>
            ) : (
              paginacao.itensPaginados.map((state) => (
                <TableRow key={state.id}>
                  <TableCell className="font-medium">{state.nome}</TableCell>
                  <TableCell className="font-mono">{state.uf}</TableCell>
                  <TableCell>{state.codigoIbge || "-"}</TableCell>
                  <TableCell>{state.pais?.nome || "-"}</TableCell>
                  <TableCell className="text-right">{state.aliquotaIbs?.toFixed(2) ?? "0.00"}%</TableCell>
                  <TableCell className="text-center">{state.isAtivo ? "Sim" : "Não"}</TableCell>
                  <TableCell>
                    {podeEditar && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(state)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(state.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingState ? "Editar Estado" : "Novo Estado"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>UF *</Label>
                <Input value={formData.uf} onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })} placeholder="SP" maxLength={2} />
              </div>
              <div className="space-y-2">
                <Label>Cód. IBGE</Label>
                <Input type="number" value={formData.codigoIbge || ""} onChange={(e) => setFormData({ ...formData, codigoIbge: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Select
                  value={formData.paisId?.toString() || ""}
                  onValueChange={(v) => setFormData({ ...formData, paisId: v ? parseInt(v) : null })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {countries?.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Alíquota IBS (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.aliquotaIbs ?? 0.10}
                onChange={(e) => setFormData({ ...formData, aliquotaIbs: parseFloat(e.target.value) || 0 })}
                placeholder="0.10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.isAtivo ?? true} onCheckedChange={(checked) => setFormData({ ...formData, isAtivo: checked })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={!formData.nome || !formData.uf || isCreating || isUpdating}>
              {isCreating || isUpdating ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir este estado? Estados com cidades vinculadas não podem ser excluídos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
